import React, { forwardRef, useContext, useEffect } from 'react'

import { update as updateIntercomConfig } from '@intercom/messenger-js-sdk'
import { IconKey } from 'govwell-ui/components/Icon/util'
import styled from 'styled-components'
import { twMerge } from 'tailwind-merge'

import { Metadata } from '@cedarjs/web'

import { useCitizenAIAssistant } from 'src/components/CitizenAIAssistant/CitizenAIAssistantContext'
import { PageTitle } from 'src/components/shared/StyledComponents'
import { useStaffAIAssistant } from 'src/components/StaffAIAssistant/StaffAIAssistantContext'
import { Flex } from 'src/govwell-ui'
import { useCurrentBreakpoint } from 'src/hooks/use-current-breakpoint'
import { useIsIntercomEnabled } from 'src/hooks/use-is-intercom-enabled'
import { usePageTitle } from 'src/hooks/use-page-title'
import {
  AISidePanelIntercomOffset,
  DefaultIntercomHorizontalPadding,
} from 'src/hooks/use-set-up-intercom'

type PageOverflowType = 'FullPage' | 'PageContentOnly' | 'None'

const LayoutContainer = styled.div<{
  $minHeight?: React.CSSProperties['minHeight']
  $maxHeight?: React.CSSProperties['maxHeight']
  $overflowY?: React.CSSProperties['overflowY']
}>`
  width: 100%;
  height: 100%;
  ${({ $minHeight }) => `min-height: ${$minHeight ?? 'auto'};`};
  ${({ $maxHeight }) => ($maxHeight ? `max-height: ${$maxHeight};` : '')};
  ${({ $overflowY }) => ($overflowY ? `overflow-y: ${$overflowY};` : '')};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;

  @media print {
    height: auto;
    overflow-y: visible;
  }
`

type PageProps = {
  children: React.ReactNode
  className?: string
  /**
   * defaults to 'stretch' - controls whether the page content grows to accommodate contents or if overflow causes a scrollbar to appear with the header being sticky
   */
  pageOverflowType?: PageOverflowType
  /**
   * Optional right sidebar content. Will be rendered as an aside element with 400px width.
   * Hidden on small screens.
   */
  rightSidebar?: React.ReactNode
}
const Page = ({ className, children, pageOverflowType = 'FullPage', rightSidebar }: PageProps) => {
  const { isSmallScreen } = useCurrentBreakpoint()
  const showSidebar = rightSidebar && !isSmallScreen()

  const isIntercomEnabled = useIsIntercomEnabled()
  const staffAIContext = useStaffAIAssistant()
  const citizenAIContext = useCitizenAIAssistant()
  const isSideViewOpen =
    ((staffAIContext?.overlay.isOpen && staffAIContext?.viewMode === 'side') ||
      (citizenAIContext?.overlay.isOpen && citizenAIContext?.viewMode === 'side')) ??
    false

  useEffect(() => {
    if (isIntercomEnabled) {
      let horizontalPadding = DefaultIntercomHorizontalPadding
      if (isSideViewOpen) {
        horizontalPadding += AISidePanelIntercomOffset
      }
      if (showSidebar) {
        horizontalPadding += 450
      }
      updateIntercomConfig({ horizontal_padding: horizontalPadding })
    }
  }, [showSidebar, isIntercomEnabled, isSideViewOpen])

  return (
    <PageContext.Provider value={{ pageOverflowType }}>
      <Flex direction="row" width="100%" height="100%" className={className}>
        <LayoutContainer
          {...(pageOverflowType === 'PageContentOnly'
            ? {
                $minHeight: '0',
                $maxHeight: '100%',
              }
            : pageOverflowType === 'FullPage'
              ? {
                  $overflowY: 'auto',
                }
              : {})}
        >
          {children}
        </LayoutContainer>
        {showSidebar && <aside className="h-full w-[400px] bg-white">{rightSidebar}</aside>}
      </Flex>
    </PageContext.Provider>
  )
}

type PageHeaderProps = {
  actions?: React.ReactNode
  icon?: IconKey | null
  noPadding?: boolean
  showRouterBackButton?: boolean
  subtitle?: React.ReactNode
  title: string // Do not turn this into "React.ReactNode", it will crash the Metadata component
  customTitleDisplay?: React.ReactNode
} & (
  | {
      backButtonHref?: string
      onBackButtonClick?: never
    }
  | {
      backButtonHref?: never
      onBackButtonClick: () => void
    }
)
const PageHeader = ({ noPadding, ...props }: PageHeaderProps) => {
  const { pageOverflowType } = usePageContext()
  const pageTitle = usePageTitle(props.title)
  return (
    <header
      style={{
        width: '100%',
      }}
      className={`${noPadding ? '' : `pt-4 pr-4 pl-4`} ${pageOverflowType === 'PageContentOnly' ? 'border-b border-neutral-200 pb-4' : ''}`}
    >
      <Metadata title={pageTitle} />
      <PageTitle {...props} title={props.customTitleDisplay || props.title} />
    </header>
  )
}

type PageContentProps = {
  children: React.ReactNode
  className?: string
}
const PageContent = ({ children, className }: PageContentProps) => {
  const { pageOverflowType } = usePageContext()
  return (
    <main
      className={twMerge(
        'flex flex-1 flex-col gap-6 p-4 md:p-6 print:h-auto print:overflow-y-visible',
        ...(pageOverflowType === 'PageContentOnly' ? ['overflow-y-auto'] : []),
        className
      )}
    >
      {children}
    </main>
  )
}

const StyledFooter = styled.footer<{
  $justifyContent: React.CSSProperties['justifyContent']
  $padding: React.CSSProperties['padding']
}>`
  position: sticky;
  bottom: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.colorWhite};
  padding: ${({ $padding }) => $padding ?? '12px'};
  display: flex;
  align-items: center;
  justify-content: ${({ $justifyContent }) => $justifyContent};
  gap: 8px;
  box-shadow: ${({ theme }) => theme.boxShadowSecondary};
  box-sizing: border-box;
`
type PageFooterProps = {
  children?: React.ReactNode
  justifyContent?: React.CSSProperties['justifyContent']
  padding?: React.CSSProperties['padding']
}
const PageFooter = forwardRef<HTMLElement, PageFooterProps>(
  ({ children, justifyContent, padding }, ref) => (
    <StyledFooter $justifyContent={justifyContent} $padding={padding} ref={ref}>
      {children}
    </StyledFooter>
  )
)

type PageContextType = {
  pageOverflowType: PageOverflowType
}
const PageContext = React.createContext<PageContextType>({} as PageContextType)
const usePageContext = () => useContext(PageContext)

export default Object.assign(Page, {
  Header: PageHeader,
  Content: PageContent,
  Footer: PageFooter,
})
